<template>
  <b-nav-item-dropdown
    right
    toggle-class="d-flex align-items-center dropdown-notification mr-25"
    menu-class="notification-dropdown-menu"
  >
    <template #button-content>
      <feather-icon
        :badge="unreadCount > 99 ? '99+' : (unreadCount || null)"
        badge-classes="bg-danger"
        icon="BellIcon"
        size="21"
      />
    </template>

    <li class="notification-dropdown-header">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h4 class="mb-0">
            Notifications
          </h4>
          <small class="text-muted">Latest activity</small>
        </div>
        <b-badge
          pill
          variant="light-primary"
        >
          {{ unreadCount }} New
        </b-badge>
      </div>
    </li>

    <li class="notification-dropdown-actions">
      <b-button
        variant="outline-primary"
        size="sm"
        @click.stop="onMarkAllRead"
      >
        <feather-icon icon="CheckCircleIcon" />
        <span class="align-middle ml-50">Mark read</span>
      </b-button>
      <b-button
        variant="outline-secondary"
        size="sm"
        :to="{ path: '/apps/notifications' }"
      >
        <feather-icon icon="Maximize2Icon" />
        <span class="align-middle ml-50">View all</span>
      </b-button>
    </li>

    <li class="notification-dropdown-list">
      <div
        v-if="latestNotifications.length === 0"
        class="notification-empty"
      >
        <feather-icon
          icon="BellOffIcon"
          size="28"
          class="mb-50"
        />
        No notifications yet
      </div>

      <button
        v-for="notification in latestNotifications"
        :key="notification.notification_id"
        type="button"
        class="notification-card"
        :class="{ 'is-unread': !notification.notification_is_read }"
        @click.stop="onClickNotification(notification)"
      >
        <span
          class="notification-dot"
          :class="{ invisible: notification.notification_is_read }"
        />
        <b-avatar
          size="34"
          :variant="typeVariant(notification.notification_type)"
          class="notification-avatar"
        >
          <feather-icon :icon="typeIcon(notification.notification_type)" />
        </b-avatar>
        <span class="notification-copy">
          <span class="notification-title-text">
            {{ notification.notification_title || '-' }}
          </span>
          <span class="notification-message">
            {{ truncateText(notification.notification_body) }}
          </span>
          <span class="notification-time">
            {{ formatTime(notification.notification_created_at) }}
          </span>
        </span>
        <b-button
          v-b-tooltip.hover
          title="Archive"
          variant="flat-secondary"
          size="sm"
          class="notification-archive"
          @click.stop="onArchive(notification)"
        >
          <feather-icon
            icon="ArchiveIcon"
            size="15"
          />
        </b-button>
      </button>
    </li>

    <li class="notification-dropdown-footer">
      <b-button
        variant="primary"
        block
        :to="{ path: '/apps/notifications' }"
      >
        Show all notifications
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BBadge, BAvatar, BButton, VBTooltip,
} from 'bootstrap-vue'
import useNotifications from '@/composables/useNotifications'

export default {
  components: {
    BNavItemDropdown,
    BBadge,
    BAvatar,
    BButton,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const {
      notifications, unreadCount, fetchNotifications, markRead, markAllRead, markAllUnread, archive,
    } = useNotifications()

    return {
      notifications, unreadCount, fetchNotifications, markRead, markAllRead, markAllUnread, archive,
    }
  },
  computed: {
    latestNotifications() {
      return this.notifications.slice(0, 5)
    },
  },
  created() {
    this.fetchNotifications({ limit: 5 })
  },
  methods: {
    typeVariant(type) {
      return {
        success: 'light-success',
        warning: 'light-warning',
        urgent: 'light-danger',
      }[type] || 'light-info'
    },
    typeIcon(type) {
      return {
        success: 'CheckCircleIcon',
        warning: 'AlertTriangleIcon',
        urgent: 'AlertOctagonIcon',
      }[type] || 'BellIcon'
    },
    formatTime(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const diffMs = Date.now() - date.getTime()
      const diffMin = Math.round(diffMs / 60000)
      if (diffMin < 1) return 'Just now'
      if (diffMin < 60) return `${diffMin}m ago`
      const diffHour = Math.round(diffMin / 60)
      if (diffHour < 24) return `${diffHour}h ago`
      const diffDay = Math.round(diffHour / 24)
      if (diffDay < 7) return `${diffDay}d ago`
      return date.toLocaleDateString()
    },
    truncateText(value) {
      if (!value) return '-'
      const text = String(value)
      return text.length > 150 ? `${text.slice(0, 150)}...` : text
    },
    async onClickNotification(notification) {
      if (!notification.notification_is_read) {
        try {
          await this.markRead(notification.notification_id)
        } catch (e) {
          // Non-fatal - still navigate even if the read-mark call failed.
        }
      }
      if (notification.notification_link) {
        this.$router.push(notification.notification_link)
      }
    },
    async onArchive(notification) {
      try {
        await this.archive(notification.notification_id)
      } catch (e) {
        this.$toast.error('Failed to archive notification')
      }
    },
    async onMarkAllRead() {
      try {
        await this.markAllRead()
      } catch (e) {
        this.$toast.error('Failed to mark all as read')
      }
    },
    async onMarkAllUnread() {
      try {
        await this.markAllUnread()
      } catch (e) {
        this.$toast.error('Failed to mark all as unread')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.notification-dropdown-header {
  padding: 1rem 1.1rem 0.9rem;
  border-bottom: 1px solid #ebe9f1;
  background: linear-gradient(180deg, #fff 0%, #fbfbfd 100%);
}
.notification-dropdown-header h4 {
  color: #5e5873;
  font-size: 1rem;
  font-weight: 600;
}
.notification-dropdown-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.65rem;
  padding: 0.75rem;
  border-bottom: 1px solid #ebe9f1;
}
.notification-dropdown-actions .btn {
  flex: 1;
}
.notification-dropdown-list {
  display: block;
  max-height: none;
  overflow-x: hidden;
  overflow-y: visible;
}
.notification-card {
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  border: 0;
  border-bottom: 1px solid #f0eef6;
  background: #fff;
  padding: 0.95rem 0.85rem 0.95rem 0.75rem;
  text-align: left;
  color: inherit;
  transition: background-color 0.15s ease;
}
.notification-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.85rem;
  bottom: 0.85rem;
  width: 3px;
  border-radius: 0 4px 4px 0;
  background: transparent;
}
.notification-card:hover {
  background: #f8f8fb;
}
.notification-card.is-unread {
  background: #f7f6ff;
}
.notification-card.is-unread::before {
  background: #7367f0;
}
.notification-card:focus {
  outline: none;
}
.notification-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #7367f0;
  flex-shrink: 0;
  margin: 14px 0.65rem 0 0;
}
.invisible {
  visibility: hidden;
}
.notification-avatar {
  flex-shrink: 0;
  margin-right: 0.75rem;
}
.notification-copy {
  display: block;
  flex: 1;
  min-width: 0;
}
.notification-title-text,
.notification-message,
.notification-time {
  display: block;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.notification-title-text {
  color: #5e5873;
  font-weight: 600;
  line-height: 1.35;
}
.notification-message {
  color: #6e6b7b;
  font-size: 0.86rem;
  line-height: 1.4;
  margin-top: 0.2rem;
}
.notification-time {
  color: #82868b;
  font-size: 0.78rem;
  margin-top: 0.45rem;
}
.notification-archive {
  flex-shrink: 0;
  margin-left: 0.35rem;
  opacity: 0.72;
}
.notification-card:hover .notification-archive {
  opacity: 1;
}
.notification-empty {
  display: flex;
  min-height: 145px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #82868b;
  text-align: center;
}
.notification-dropdown-footer {
  padding: 0.85rem;
}
</style>

<style lang="scss">
.notification-dropdown-menu {
  width: 390px;
  max-width: calc(100vw - 1.5rem);
  padding: 0;
  overflow-x: hidden;
  border: 0;
  box-shadow: 0 10px 30px rgba(34, 41, 47, 0.16);
}
</style>
